import React from 'react';
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Home, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryPageProps {
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export function ErrorBoundaryPage({ error, errorInfo }: ErrorBoundaryPageProps) {
  const navigate = useNavigate();
  const duckFacts = [
    "Looks like a duck flew into the server room!",
    "Even the best ducks have off days. This one just had one.",
    "Our ducks are working hard to fix this quacking issue.",
    "Error 500: Duck not found in the expected location.",
  ];
  
  const randomDuckFact = duckFacts[Math.floor(Math.random() * duckFacts.length)];

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 py-12 sm:px-6 lg:px-8 bg-gradient-to-b from-accent/5 to-accent/10 dark:from-accent/5 dark:to-accent/10">
      <div className="text-center w-full max-w-xl mx-auto p-6 sm:p-8">
        <div className="w-64 h-48 mx-auto mb-8 flex items-center justify-center">
          <img 
            src="/assets/img/500.png" 
            alt="Welder duck" 
            className="h-full w-auto object-contain"
          />
        </div>
        
        <div className="bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 p-4 rounded-lg mb-6 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" />
          <div>
            <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
            <p className="text-left">An unexpected error occurred while loading this page.</p>
          </div>
        </div>
        
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
          {randomDuckFact} Don't worry, it's probably our fault. Our team has been notified.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            onClick={() => navigate(-1)}
            variant="outline"
            className="gap-2 bg-white dark:bg-secondary"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </Button>
          <Button 
            onClick={() => navigate('/')}
            className="gap-2 bg-primary"
          >
            <Home className="w-4 h-4" />
            Return Home
          </Button>
        </div>
        
        {import.meta.env.DEV && error && (
          <details className="mt-8 text-center">
            <summary className="text-sm text-gray-500 dark:text-gray-400 cursor-pointer mb-2">
              Error Details (only visible in development)
            </summary>
            <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded text-xs overflow-x-auto">
              {error.toString()}
              {errorInfo?.componentStack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export default ErrorBoundaryPage;
